/**
 * iOS-spec preloaded cost categories.
 *
 * VERBATIM port of `/tmp/ios/repo/frontend/app/tools/cost-tracker.tsx`
 * `COST_CATEGORIES` dict so the web Cost Tracker offers the same picker
 * options as the mobile app.
 *
 * Each top-level key is a CATEGORY (used in `expense.categoryName`).
 * The array values are typeahead suggestions for the expense title.
 */

export const IOS_COST_CATEGORIES = {
  'Site & Surveys': [
    'Soil test', 'Geotechnical report', 'Feature and level survey', 'Contour survey', 'Site survey',
  ],
  'Site Preparation': [
    'Demolition', 'Tree removal', 'Temporary fencing', 'Site clearing', 'Earthworks',
    'Site cut', 'Fill removal', 'Retaining walls', 'Stormwater management',
  ],
  'Connections': [
    'Stormwater connection', 'Sewer connection', 'Water connection', 'Gas connection',
    'Electrical connection', 'NBN connection', 'Footpath crossover', 'Driveway crossover',
  ],
  'Approvals & Design': [
    'Development approval', 'Planning approval', 'Building rules consent', 'Engineering drawings',
    'Energy rating assessment', 'Bushfire report', 'Flood report', 'Private certifier',
    'Water authority approval', 'Drafting', 'Architectural design', 'Interior design consultation',
    'Structural engineering', 'Civil engineering', 'Building consultant', 'Builder advisory service',
  ],
  'Legal & Finance': [
    'Conveyancing', 'Legal review', 'Finance fees', 'Loan application fees',
    'Lenders mortgage insurance', 'Construction insurance',
  ],
  'Pre-Construction': ['Temporary site power', 'Temporary water supply'],
  'Structure': ['Slab', 'Footings', 'Structural steel', 'Framing', 'Upper floor framing', 'Roof structure'],
  'External Walls': ['Brickwork', 'Render', 'Cladding', 'Feature stone', 'Feature timber', 'Façade upgrade'],
  'Doors & Entry': ['Garage door', 'Front door upgrade'],
  'Roofing': ['Roof tiles', 'Colorbond roofing', 'Roof sarking', 'Roof insulation', 'Guttering', 'Downpipes'],
  'Windows & Doors': [
    'Aluminium windows', 'Double glazing', 'Sliding doors', 'Bi fold doors',
    'Stacker doors', 'Security screens', 'Flyscreens',
  ],
  'Insulation': ['Wall insulation', 'Ceiling insulation', 'Acoustic insulation'],
  'Electrical': [
    'Downlights', 'Pendant lights', 'Feature lighting', 'Exterior lighting',
    'Power points', 'USB outlets', 'TV points', 'Data points', 'Switchboard upgrade', 'Smart home wiring',
  ],
  'Security': ['Security system', 'CCTV', 'Intercom', 'Doorbell'],
  'Solar & Energy': ['Solar system', 'Solar battery'],
  'Hot Water': ['Hot water system', 'Gas hot water', 'Electric hot water', 'Heat pump hot water'],
  'Water Systems': ['Water filtration system', 'Rainwater tank', 'External taps'],
  'Heating & Cooling': [
    'Ducted air conditioning', 'Split system air conditioning', 'Evaporative cooling',
    'Gas heating', 'Electric heating', 'Underfloor heating', 'Ceiling fans',
  ],
  'Kitchen': [
    'Kitchen cabinetry', 'Soft close drawers', 'Stone benchtops', 'Laminate benchtops',
    'Waterfall benchtop', 'Appliance upgrades', 'Oven upgrade', 'Cooktop upgrade',
    'Rangehood', 'Dishwasher', 'Microwave provision', 'Butlers pantry', 'Pantry fit out', 'Splashback',
  ],
  'Bathroom': [
    'Bathroom tiles', 'Full height tiling', 'Shower niche', 'Frameless shower screen',
    'Semi frameless shower', 'Freestanding bath', 'Vanity upgrade', 'Stone vanity top',
    'Tapware upgrade', 'Rainfall shower', 'Heated towel rails', 'Bathroom mirrors', 'Shaving cabinet',
  ],
  'Flooring': [
    'Carpet', 'Timber flooring', 'Hybrid flooring', 'Vinyl plank flooring',
    'Floor tiling', 'Feature floor tiles', 'Skirting upgrade',
  ],
  'Painting & Finishes': [
    'Internal painting', 'External painting', 'Feature wall paint',
    'Wallpaper', 'Decorative mouldings', 'Wainscoting',
  ],
  'Outdoor Living': [
    'Decking', 'Veranda', 'Pergola', 'Alfresco upgrade',
    'Outdoor kitchen', 'Outdoor blinds', 'Outdoor ceiling heaters',
  ],
  'Landscaping': [
    'Turf', 'Artificial grass', 'Irrigation system', 'Garden beds', 'Planting', 'Mulch',
    'Garden edging', 'Concrete paths', 'Exposed aggregate driveway', 'Paving', 'Fencing', 'Side gates',
  ],
  'Pool & Spa': ['Pool', 'Spa', 'Pool fencing', 'Pool heating'],
  'Storage': [
    'Built in robes', 'Walk in robe fit out', 'Linen cupboard fit out', 'Garage shelving',
    'Attic storage', 'Under stair storage', 'Garden shed', 'Workshop', 'Carport',
  ],
  'Window Furnishings': ['Blinds', 'Curtains', 'Plantation shutters'],
  'Finishing Touches': ['Security doors', 'Letterbox', 'Clothesline'],
  'Moving & Insurance': ['Removalist costs', 'Home insurance', 'Building insurance', 'Maintenance allowance'],
  'Custom': ['Custom item'],
};

// 12-colour palette mirrors the brand spec, deterministic by category name
const PALETTE = [
  '#1d8efe', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#64748b',
  '#06b6d4', '#a16207', '#ef4444', '#22c55e', '#6366f1', '#0891b2',
];
const hashString = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    // eslint-disable-next-line no-bitwise
    h = ((h << 5) - h) + s.charCodeAt(i);
    // eslint-disable-next-line no-bitwise
    h |= 0;
  }
  return Math.abs(h);
};
export const colorForCategoryName = (name) => PALETTE[hashString(String(name || '')) % PALETTE.length];

/** Array form (id = -<index> so it can't collide with Supabase numeric ids). */
export const IOS_CATEGORY_LIST = Object.keys(IOS_COST_CATEGORIES).map((name, i) => ({
  id: -(i + 1),
  name,
  color: colorForCategoryName(name),
  items: IOS_COST_CATEGORIES[name],
}));

/** Flat list of every line-item across every category, used for typeahead. */
export const ALL_COST_ITEMS = Object.values(IOS_COST_CATEGORIES).flat();
